import { useLazyApiV1CorporationsFlagsRetrieveQuery } from "@/services/api";
import { useAppDispatch, useAppSelector } from "@/services/store";
import { rtkErrorToStr, sleep } from "@/utils";
import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import * as React from "react";
import { FeatureFlagError, featureFlagsSlice } from "./feature-flags.slice";

const { success, error, loading } = featureFlagsSlice.actions;

export type FeatureFlag = "disable_mapping_download";

export function useFeatureFlag(flag: FeatureFlag): boolean {
  const flags = useAppSelector(state => state.featureFlags);
  // Note in the case where flags have failed to load, we should assume all are false
  // since based on the backend, flag "enables" a behaviour
  // this is an unlikely event, but it's better to be safe than sorry
  return flags.data[flag] ?? false;
}

const MAX_FAILURE_COUNT = 10;

/**
 * Takes in tenantId and performs fetching to load feature flags into redux store.
 * @param corporationId
 */
export function useFeatureFlagsLoader(corporationId: string | null | undefined) {
  const flags = useAppSelector(state => state.featureFlags);
  const [trigger] = useLazyApiV1CorporationsFlagsRetrieveQuery();
  const dispatch = useAppDispatch();

  React.useEffect(() => {
    if (corporationId) {
      dispatch(loading());
    }
  }, [corporationId]);

  React.useEffect(() => {
    if (!corporationId) {
      return;
    }
    // The other react effect will trigger the fetching here.
    if (flags.status === "success" || flags.status === "idle") {
      return;
    }

    function loadData(id: string, failureCount: number) {
      return trigger({ id })
        .unwrap()
        .then(data => {
          dispatch(success(data.flags));
        })
        .catch((e: FetchBaseQueryError | SerializedError) => {
          dispatch(error({ message: rtkErrorToStr(e), failureCount }));
          console.error(e);
        });
    }

    if (flags.status === "loading") {
      loadData(corporationId, 1);
    }
    if (flags.status === "error") {
      const { failureCount } = flags.error as FeatureFlagError;
      if (failureCount >= MAX_FAILURE_COUNT) {
        return;
      }
      // 2 ^ failureCount creates exponential backoff
      // it's unlikely to run, but if some global issue occurs, it prevents clients
      // from DDoSing the server
      sleep(2 ** failureCount * 1000).then(_ => loadData(corporationId, failureCount + 1));
    }
  }, [corporationId, flags]);
}
